import Purchases, {
  type CustomerInfo,
  type PurchasesPackage,
} from "react-native-purchases";
import { Alert } from "react-native";
import * as Sentry from "@sentry/react-native";

/**
 * Checks if the user has any active subscription entitlements
 * @param customerInfo RevenueCat customer info
 * @returns Whether the user has any active entitlements
 */
export const hasActiveSubscription = (customerInfo: CustomerInfo): boolean => {
  const entitlements = customerInfo.entitlements.active;
  return Object.keys(entitlements).length > 0;
};

export interface PurchaseResult {
  success: boolean;
  hasActiveSubscription: boolean;
  customerInfo?: CustomerInfo;
  error?: Error;
}

/**
 * Handles the purchase of a RevenueCat package
 * @param packageId ID of the package to purchase
 * @param availablePlans Available purchase plans
 * @param showAlerts Whether to show success/error alerts
 * @returns Purchase result object
 */
export const handlePurchase = async (
  packageId: string,
  availablePlans: PurchasesPackage[],
  showAlerts = true,
): Promise<PurchaseResult> => {
  // Find the selected package
  const selectedPackage = availablePlans.find(
    (plan) => plan.identifier === packageId,
  );

  if (!selectedPackage) {
    if (showAlerts) {
      Alert.alert(
        "Oops",
        "The selected subscription plan is not available. Please try again or reach out to support@getkaiapp.com",
      );
    }
    return {
      success: false,
      hasActiveSubscription: false,
      error: new Error("Selected package not found"),
    };
  }

  try {
    // Purchase the package
    const { customerInfo } = await Purchases.purchasePackage(selectedPackage);
    const hasEntitlements = hasActiveSubscription(customerInfo);

    // Show success message if requested
    if (hasEntitlements && showAlerts) {
      Alert.alert(
        "Success",
        "Thank you for your subscription! You now have access to premium features.",
      );
    }

    return {
      success: true,
      hasActiveSubscription: hasEntitlements,
      customerInfo,
    };
  } catch (error: unknown) {
    // Don't capture errors when user cancels
    if (
      error instanceof Error &&
      !("userCancelled" in error && error.userCancelled)
    ) {
      Sentry.captureException(error);

      // Show error if requested
      if (showAlerts) {
        Alert.alert(
          "Purchase Failed",
          error.message ||
            "There was a problem with your purchase. Please try again.",
        );
      }
    }

    return {
      success: false,
      hasActiveSubscription: false,
      error: error instanceof Error ? error : new Error(String(error)),
    };
  }
};
